import React from 'react';

import { DropdownOwnerships } from '../../../../../../shared/components/ui/atoms';

import { ResumeContentContext } from '../../types';

/**
 * Renderiza el contenido básico solo con state
 */
export const renderStateOnly = (
  steps: ResumeContentContext['steps'],
  isBrokerMode: boolean,
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div data-testid="render-state-only">
      {isBrokerMode ? (
        <>Show me loan terms for a property {locationPreposition} {steps.state.element}</>
      ) : (
        <>Show me loan terms for {steps.state.element}</>
      )}
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido con state y loanType
 */
export const renderStateAndLoanType = (
  steps: ResumeContentContext['steps'],
  isBrokerMode: boolean,
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div data-testid="render-state-and-loan-type">
      {isBrokerMode ? (
        <>Show me {steps.loanType.element} loan terms for a property {locationPreposition} {steps.state.element}</>
      ) : (
        <>Show me {steps.loanType.element} loan terms {locationPreposition} {steps.state.element}</>
      )}
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido con state, loanType y loanOwner
 */
export const renderStateLoanTypeAndOwner = (
  steps: ResumeContentContext['steps'],
  isBrokerMode: boolean,
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div data-testid="render-state-loan-type-and-owner">
      {isBrokerMode
        ? <>Show me {steps.loanType.element} loan terms for a property {steps.loanOwner.element} {locationPreposition} {steps.state.element}</>
        : <>Show me {steps.loanType.element} loan terms for a {steps.loanOwner.element} {locationPreposition} {steps.state.element}</>
      }
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido con state, loanType, loanOwner y propertyType
 */
export const renderWithPropertyType = (
  steps: ResumeContentContext['steps'],
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div data-testid="render-with-property-type">
      Show me {steps.loanType.element} loan terms for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido con FICO score (modo no broker)
 */
export const renderWithFicoNonBroker = (
  steps: ResumeContentContext['steps'],
  oneLine: boolean,
  locationPreposition: 'in' | 'at'
) => {
  if (oneLine) {
    return (
      <React.Fragment>
        <div data-testid="render-with-fico-non-broker-oneline">
          Show me {steps.loanType.element} loan terms for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}. My FICO score is {steps.ficoRange.element}
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div data-testid="render-with-fico-non-broker-multiline">
        Show me {steps.loanType.element} loan terms for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}.
      </div>
      <div data-testid="render-with-fico-non-broker-fico">My FICO score is {steps.ficoRange.element}</div>
    </React.Fragment>
  );
};

/**
 * Renderiza el contenido con FICO score (modo broker)
 */
export const renderWithFicoBroker = (
  steps: ResumeContentContext['steps'],
  oneLine: boolean,
  locationPreposition: 'in' | 'at'
) => {
  if (oneLine) {
    return (
      <React.Fragment>
        <div className='text-summary' data-testid="render-with-fico-broker-oneline">
          Show me {steps.loanType.element} loan terms for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}. The borrower has a FICO score of {steps.ficoRange.element}
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div className='text-summary' data-testid="render-with-fico-broker-multiline-summary">
        Show me {steps.loanType.element} loan terms for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}
      </div>
      <div className='text-info-borrower' data-testid="render-with-fico-broker-multiline-info">
        The borrower has a FICO score of {steps.ficoRange.element} {`and has purchased/sold`} {steps.propertiesNegotiated.element} {`in the past 36 months`}
      </div>
      <div className='text-new-borrower' data-testid="render-with-fico-broker-multiline-new">
        Using <span style={{ textDecoration: 'none' }}>{`a new borrower's`}</span> FICO Score and experience
      </div>
    </React.Fragment>
  );
};

/**
 * Renderiza el contenido completo con borrowerData (modo no broker, multilínea)
 */
export const renderCompleteWithBorrowerData = (
  steps: ResumeContentContext['steps'],
  ownershipItems: any[],
  hasOwnerships: boolean,
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div className='text-summary' data-testid="render-complete-with-borrower-data-summary">
      Show me {steps.loanType.element} loan terms for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}
    </div>
    <div className='text-info-borrower' data-testid="render-complete-with-borrower-data-info">
      {`I'm borrowing with a FICO score of`} {steps.ficoRange.element} {`and have purchased/sold`} {steps.propertiesNegotiated.element} {`in the past 36 months`}
    </div>
    {hasOwnerships && (
      <div className='text-borrower' data-testid="render-complete-with-borrower-data-ownership">
        Using
        <DropdownOwnerships
          items={ownershipItems}
        />
        {`FICO Score and experience`}
      </div>
    )}
  </React.Fragment>
);

/**
 * Renderiza el contenido completo sin borrowerData (modo no broker, multilínea)
 */
export const renderCompleteWithoutBorrowerData = (
  steps: ResumeContentContext['steps'],
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div data-testid="render-complete-without-borrower-data-summary">
      Show me {steps.loanType.element} loan terms for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}
    </div>
    <div data-testid="render-complete-without-borrower-data-info">
      The borrower has a FICO score of {steps.ficoRange.element} {`and has purchased/sold`} {steps.propertiesNegotiated.element} {`in the past 36 months`}
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido completo en una línea (modo broker)
 */
export const renderOneLineBroker = (
  steps: ResumeContentContext['steps'],
  isBrokerResume: boolean,
  locationPreposition: 'in' | 'at'
) => {
  if (isBrokerResume) {
    return (
      <React.Fragment>
        <div data-testid="render-one-line-broker-resume">
          My borrower wants a {steps.loanType.element} loan for a {steps.propertyType.element} {steps.loanOwner.element}. They have a FICO score of {steps.ficoRange.element} and have purchased/sold {steps.propertiesNegotiated.element} in the last 36 months
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div data-testid="render-one-line-broker">
        Show me {steps.loanType.element} loan terms for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}. The borrower has a FICO score of {steps.ficoRange.element} and has purchased/sold {steps.propertiesNegotiated.element} in the past 36 months
      </div>
    </React.Fragment>
  );
};

/**
 * Renderiza el contenido completo en una línea (modo first person)
 */
export const renderOneLineFirstPerson = (
  steps: ResumeContentContext['steps'],
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div data-testid="render-one-line-first-person" className='text-summary'>
      Show me {steps.loanType.element} loan terms for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}. My FICO score is {steps.ficoRange.element} and I&apos;ve purchased/sold {steps.propertiesNegotiated.element} in the last 36 months
    </div>
  </React.Fragment>
);

/**
 * Renderiza el contenido completo en una línea (modo normal)
 */
export const renderOneLineNormal = (
  steps: ResumeContentContext['steps'],
  locationPreposition: 'in' | 'at'
) => (
  <React.Fragment>
    <div data-testid="render-one-line-normal">
      Show me {steps.loanType.element} loan terms for a {steps.propertyType.element} {steps.loanOwner.element} {locationPreposition} {steps.state.element}. My FICO score is {steps.ficoRange.element} and I&apos;ve {steps.loanOwner.element} {steps.propertiesNegotiated.element} in the past 36 months
    </div>
  </React.Fragment>
);

